import * as React from 'react';
import { Overview } from './components/overview';
import { SubjectWeeklyTrendChart } from './components/SubjectWeeklyTrendChart';
import { Authorization } from '../misc/components/Authorization';
import { useSecurityContext } from '../Core/SecurityContext';
import { useCurrentStudyConfigurationContext } from '../Core/CurrentStudyConfigurationContext';
import { PermissionEnum } from '../misc/api/ApiPermissionEnum';
import LoadingWithMessage from '../misc/components/LoadingWithMessage';
import { ErrorBoundary } from '../errors/ErrorBoundary';
import { useWebPortalLegacyClient } from '../misc/api/WebPortalTokenClient';
import { HasPermission } from '../misc/components/Authorization';

declare const rootUrl;

export interface IndexViewModel {
    Progress: StudyProgressModel;
    GraphData: SubjectDailyTrendModel;
}

export interface StudyProgressModel {
    Study: string;
    LastSession: string;
    ExpectedSubjectCount: number;
    CurrentSubjectCount: number;
    ExpectedSessionCount: number;
    CurrentSessionCount: number;
    SubjectPercentage: number;
    SessionsPercentage: number;
}

export interface SubjectDailyTrendModel {
    Days: Array<SubjectDailyTrendDayModel>;
}

export interface SubjectDailyTrendDayModel {
    Day: string;
    Daily: number;
    RunningTotal: number;
}

export interface ReceiveDataState {
    overallProgress: number;
    isGraphVisible: boolean;
    graphData: SubjectDailyTrendModel;
    isFetching: boolean;
    lastUpdated: number;
}

interface ProgressWidgetProps {
    fetching: boolean;
    progress: StudyProgressModel;
    graphData: SubjectDailyTrendModel;
    onRefresh: () => void;
}

export const ProgressWidget = ({ fetching, progress, graphData, onRefresh }: ProgressWidgetProps) => {   
    if (fetching) {
        return <LoadingWithMessage message="Loading Study Progress..." />;
    } else {
        const isGraphVisible = graphData !== undefined && graphData.Days !== null;

        return (
            <>
                <Overview OverallProgress={progress} />
                <SubjectWeeklyTrendChart visible={isGraphVisible} data={graphData} />
                <button className="btn btn-primary" onClick={onRefresh}>Refresh</button>
            </>
        );
    }
}

export const ProgressWidgetApp = () => {
    const [graphData, setGraphData] = React.useState<SubjectDailyTrendModel>();
    const [progress, setProgress] = React.useState<StudyProgressModel>();
    const [fetching, setFetching] = React.useState<boolean>(false);
    const { currentUserPermissions } = useSecurityContext();
    const { study } = useCurrentStudyConfigurationContext();
    const legacyClient = useWebPortalLegacyClient();

    const refresh = async () => {
        setFetching(true);
        legacyClient.progressWidgetInit(study.id)
            .then(json => {
                setFetching(false);
                setProgress(json.Progress);
                setGraphData(json.GraphData);
            });
    }

    React.useEffect(() => {
        if (!HasPermission(currentUserPermissions, study.id, PermissionEnum.ViewStudyProgressWidget))
            return;

        refresh();
    }, []);

    const handleClick = () => {
        refresh();
    }

    return (
        <Authorization studyId={study.id} currentPermissions={currentUserPermissions} permissionId={PermissionEnum.ViewStudyProgressWidget}>
            <ErrorBoundary>
                <div className="col-md-6">
                    <div id="StudyProgressWidget" className="ibox">
                        <div className="ibox-title">
                            <h5 className="text-primary">Study Progress</h5>
                        </div>
                        <div className="ibox-content">
                            <ProgressWidget fetching={fetching} progress={progress} graphData={graphData} onRefresh={handleClick} />
                        </div>
                    </div>
                </div>
            </ErrorBoundary>
        </Authorization>
    );
}
